import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  LockOpen as ReopenIcon,
  Refresh as RefreshIcon,
  TaskAlt as CloseIcon,
  VisibilityOutlined as ViewIcon,
  DeleteOutline as RemoveIcon,
  CancelOutlined as CancelPalletIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import {
  fulfillmentApiBase,
  fulfillmentListPage,
  withAccountBody,
  withAccountParams,
} from '../../utils/fulfillmentApi.mjs';
import usePrinter from '../../hooks/usePrinter';
import { cargoTypeInfo, PALLET_STATUS_FILTERS } from '../../pages/fulfillmentUi.mjs';
import ConfirmDialog from './ConfirmDialog';
import FulfillmentStatusChip from './FulfillmentStatusChip';
import { readPalletPrintAfterClose } from '../../pages/fulfillmentUi.mjs';
import FulfillmentProductThumb from './FulfillmentProductThumb';
import PalletCloseDialog from './PalletCloseDialog';
import { newPrintIdempotencyKey } from '../../utils/printClient';
import { useNotify } from '../NotifyProvider';

const PAGE_SIZE_KEY = 'fulfillment_rows_per_page';
const TABLE_SX = {
  minWidth: 760,
  '& .MuiTableCell-body': { py: 0.625 },
  '& .MuiChip-root': { height: 22 },
  '& .MuiChip-label': { px: 0.875 },
};

function formatPanelError(error) {
  const data = error?.response?.data;
  const base = data?.message || error.message || 'Ошибка';
  const departed = data?.details?.departed_orders;
  if (!Array.isArray(departed) || !departed.length) return base;
  const lines = departed.slice(0, 12).map((row) => {
    const id = row.posting_number ?? row.wb_order_id ?? '—';
    const article = row.article ? ` · ${row.article}` : '';
    const reason = row.reason || 'выбыл';
    return `${id}${article} — ${reason}`;
  });
  const more = departed.length > 12 ? `\n…и ещё ${departed.length - 12}` : '';
  return `${base}\n${lines.join('\n')}${more}`;
}

function isDepartedPalletItem(item) {
  if (!item) return false;
  if (item.invalid && String(item.invalid_reason || '') === 'left_mp') return true;
  const task = item.task || item.posting || null;
  if (!task) return false;
  const supplier = String(task.supplier_status || '').toLowerCase();
  const wb = String(task.wb_status || '').toLowerCase();
  const ozon = String(task.ozon_status || task.status || '').toLowerCase();
  if (['cancel', 'canceled', 'cancelled', 'canceled_by_client', 'cancel_by_client'].includes(supplier)) return true;
  if (['canceled', 'cancelled', 'canceled_by_client', 'sold'].includes(wb)) return true;
  if (supplier === 'complete') return true;
  if (['cancelled', 'canceled', 'delivered', 'returned', 'not_accepted', 'cancelled_from_split'].includes(ozon)) return true;
  return false;
}

/** Номер ШК (primary) и *D-код (secondary) для колонки «Стикер». */
function palletItemStickerParts(item) {
  const number = String(item?.sticker_number || item?.task?.sticker_number || '').trim() || null;
  const barcode = String(item?.sticker_barcode || item?.task?.sticker_barcode || '').trim() || null;
  return { number, barcode };
}

/** Chip статуса позиции палеты — отдельно от колонки стикера. */
function palletItemStatusInfo(item) {
  if (!item) return { code: 'unknown', label: '—' };
  if (item.invalid && String(item.invalid_reason || '') === 'left_mp') {
    return { code: 'canceled', label: 'Выбыл' };
  }
  const task = item.task || item.posting || null;
  if (!task) {
    return isDepartedPalletItem(item)
      ? { code: 'canceled', label: 'Выбыл с МП' }
      : { code: 'collected', label: 'На палете' };
  }
  const supplier = String(task.supplier_status || '').toLowerCase();
  const wb = String(task.wb_status || '').toLowerCase();
  const ozon = String(task.ozon_status || task.status || '').toLowerCase();
  if (
    ['cancel', 'canceled', 'cancelled', 'canceled_by_client', 'cancel_by_client'].includes(supplier)
    || ['canceled', 'cancelled', 'canceled_by_client'].includes(wb)
    || ['cancelled', 'canceled', 'cancelled_from_split', 'not_accepted'].includes(ozon)
  ) {
    return { code: 'canceled', label: 'Отменён' };
  }
  if (wb === 'sold' || ozon === 'delivered') {
    return { code: 'sold', label: 'Выкуплен' };
  }
  if (ozon === 'returned') {
    return { code: 'return', label: 'Возврат' };
  }
  if (supplier === 'complete' || ['delivering', 'sent_by_seller', 'acceptance_in_progress', 'driver_pickup'].includes(ozon)) {
    return { code: 'delivery', label: 'В доставке' };
  }
  if (isDepartedPalletItem(item)) {
    return { code: 'return', label: 'Выбыл с МП' };
  }
  return { code: 'collected', label: 'На палете' };
}

const cargoChipSx = {
  bgcolor: '#F0EFF4',
  color: '#777486',
  fontWeight: 700,
};

const COLUMNS = [
  { key: '__view', label: '' },
  { key: 'code', label: 'Палета', sortable: true },
  { key: 'items_count', label: 'Заказы', sortable: true },
  { key: 'warehouse_id', label: 'Склад', sortable: true },
  { key: 'status', label: 'Статус', sortable: true },
  { key: '__actions', label: '', align: 'right' },
];

function palletColumns(marketplace) {
  return COLUMNS.map((column) => {
    if (column.key !== 'items_count') return column;
    if (marketplace === 'yandex') return { ...column, label: 'Грузоместа' };
    return column;
  });
}
function initialPageSize() {
  const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return [25, 50, 100, 200].includes(saved) ? saved : 50;
}

export default function PalletsPanel({
  accountId,
  marketplace = 'wb',
  canManage = false,
  warehouseNames = {},
}) {
  const isOzon = marketplace === 'ozon';
  const isYandex = marketplace === 'yandex';
  const isLocalWarehouse = isOzon || isYandex;
  const apiBase = fulfillmentApiBase(marketplace);
  const printer = usePrinter();
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const { success: notifySuccess, error: notifyError } = useNotify();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sort, setSort] = useState({ key: 'updated_at', dir: 'desc' });
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeStep, setCloseStep] = useState('confirm');
  const [closeVerifyCode, setCloseVerifyCode] = useState('');
  const [closeError, setCloseError] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/pallets`, {
        params: withAccountParams(marketplace, accountId, {
          page: fulfillmentListPage(marketplace, page),
          page_size: rowsPerPage,
          sort_key: sort.key,
          sort_dir: sort.dir,
          ...(statusFilter ? { status: statusFilter } : {}),
        }),
      });
      setItems(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (requestError) {
      setError(formatPanelError(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, marketplace, page, rowsPerPage, sort, statusFilter]);

  useEffect(() => { setPage(0); }, [accountId, marketplace]);
  useEffect(() => { load(); }, [load]);

  const handleSort = (key) => {
    setPage(0);
    setSort((old) => ({
      key,
      dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const openDetail = async (pallet) => {
    setBusyId(pallet.id);
    try {
      const { data } = await axios.get(`${apiBase}/pallets/${pallet.id}`, {
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(data);
    } catch (requestError) {
      setError(formatPanelError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const createPallet = async () => {
    if (!isLocalWarehouse || !accountId || creating) return;
    setCreating(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.post(`${apiBase}/pallets`, withAccountBody(marketplace, accountId));
      const pallet = data?.pallet || data;
      setNotice(`Палета ${pallet?.code || pallet?.id || ''} создана`);
      notifySuccess('Палета создана');
      setPage(0);
      await load();
      if (pallet?.id) setDetail(data?.pallet ? data : pallet);
    } catch (requestError) {
      const msg = formatPanelError(requestError);
      setError(msg);
      notifyError(msg);
    } finally {
      setCreating(false);
    }
  };

  const printPalletLabel = async (pallet, { skipBusyGuard = false } = {}) => {
    const target = pallet || closeTarget;
    if (!target?.id || (!skipBusyGuard && closeBusy)) return;
    if (!printerUid) {
      const msg = 'Выберите принтер в Настройки → Печать';
      setCloseError(msg);
      notifyError(msg);
      return;
    }
    setCloseBusy(true);
    setCloseError('');
    try {
      await axios.post(
        `${apiBase}/pallets/${target.id}/print`,
        withAccountBody(marketplace, accountId, { printer_uid: printerUid }),
        { headers: { 'Idempotency-Key': newPrintIdempotencyKey('pallet-print') } },
      );
      setNotice(`Печать этикетки ${target.code || target.id} отправлена`);
      notifySuccess('Этикетка палеты отправлена в печать');
    } catch (requestError) {
      const msg = requestError?.response?.data?.message || requestError.message;
      setCloseError(msg);
      notifyError(msg);
    } finally {
      setCloseBusy(false);
    }
  };

  const openCloseDialog = (pallet) => {
    setCloseTarget(pallet);
    setCloseStep('confirm');
    setCloseVerifyCode('');
    setCloseError('');
  };

  const cancelCloseDialog = () => {
    if (closeBusy) return;
    setCloseTarget(null);
    setCloseStep('confirm');
    setCloseVerifyCode('');
    setCloseError('');
  };

  const reprintPalletLabel = async () => {
    await printPalletLabel(closeTarget);
  };

  const closePallet = async (scanned) => {
    if (!closeTarget?.id) return;
    const closed = closeTarget;
    // ARM: без скана — supervisor close. TSD (если когда-то переиспользует панель) — scanned_barcode.
    const code = String(scanned || closeVerifyCode || '').trim();
    const body = code
      ? { scanned_barcode: code }
      : { supervisor: true };
    setCloseBusy(true);
    setBusyId(closeTarget.id);
    setCloseError('');
    setError('');
    let printTarget = null;
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets/${closeTarget.id}/close`,
        withAccountBody(marketplace, accountId, body),
      );
      const pallet = data?.pallet || data;
      setNotice(`Палета ${pallet?.code || closeTarget.code} закрыта`);
      notifySuccess(`Палета ${pallet?.code || closeTarget.code} закрыта`);
      setDetail(pallet);
      setCloseTarget(null);
      setCloseStep('confirm');
      setCloseVerifyCode('');
      if (readPalletPrintAfterClose() && printerUid) printTarget = pallet?.id ? pallet : closed;
      await load();
    } catch (requestError) {
      const msg = formatPanelError(requestError);
      const code = requestError?.response?.data?.code;
      const soft = code === 'pallet_barcode_mismatch'
        || code === 'validation_error';
      if (soft) {
        setCloseError(msg);
        notifyError(msg);
        setCloseVerifyCode('');
        return;
      }
      setCloseError(msg);
      setError(msg);
      notifyError(msg);
      if (code === 'orders_left_mp' && canManage && closeTarget?.status === 'open') {
        /* UI в диалоге / detail покажет кнопку «Убрать выбывшие» */
      }
    } finally {
      setCloseBusy(false);
      setBusyId(null);
    }
    if (printTarget) await printPalletLabel(printTarget, { skipBusyGuard: true });
  };

  const askReopenPallet = (pallet) => {
    const code = pallet?.code || (pallet?.id ? `ПЛ-${pallet.id}` : 'палету');
    setConfirm({
      title: 'Переоткрыть палету',
      message: `Переоткрыть палету ${code}? Она снова станет доступна для проклейки.`,
      confirmText: 'Переоткрыть',
      onConfirm: () => {
        setConfirm(null);
        reopenPallet(pallet);
      },
    });
  };

  const askRemoveItem = (pallet, item) => {
    const orderId = item?.posting_number
      || item?.yandex_order_id
      || item?.wb_order_id
      || item?.posting?.posting_number
      || '—';
    setConfirm({
      title: 'Снять заказ с палеты',
      message: `Снять заказ № ${orderId} с палеты? Заказ вернётся в очередь проклейки.`,
      confirmText: 'Снять',
      onConfirm: () => {
        setConfirm(null);
        removeOneItem(pallet, item);
      },
    });
  };

  /** Пустую палету закрыть нельзя — её отменяют (status=cancelled). */
  const cancelPallet = async (pallet) => {
    const target = pallet || cancelTarget;
    if (!target?.id) return;
    setBusyId(target.id);
    setError('');
    try {
      await axios.post(
        `${apiBase}/pallets/${target.id}/cancel`,
        withAccountBody(marketplace, accountId),
      );
      const msg = `Палета ${target.code || target.id} отменена`;
      setNotice(msg);
      notifySuccess(msg);
      setCancelTarget(null);
      setCloseTarget(null);
      if (detail?.id === target.id) setDetail(null);
      await load();
    } catch (requestError) {
      const msg = formatPanelError(requestError);
      setError(msg);
      notifyError(msg);
      setCancelTarget(null);
    } finally {
      setBusyId(null);
    }
  };

  const reopenPallet = async (pallet) => {
    setBusyId(pallet.id);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets/${pallet.id}/reopen`,
        withAccountBody(marketplace, accountId),
      );
      const result = data?.pallet || data;
      setNotice(`Палета ${result?.code || pallet.code} переоткрыта`);
      setDetail(result);
      await load();
    } catch (requestError) {
      setError(formatPanelError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  const removeDeparted = async (pallet, { itemIds = null, fromCloseError = false } = {}) => {
    if (!pallet?.id) return;
    setBusyId(pallet.id);
    setError('');
    setCloseError('');
    try {
      const body = withAccountBody(marketplace, accountId, {
        ...(itemIds ? { item_ids: itemIds } : {}),
      });
      const { data } = await axios.post(
        `${apiBase}/pallets/${pallet.id}/remove-departed`,
        body,
      );
      const removedCount = Number(data?.removed_count) || (data?.removed || []).length || 0;
      const result = data?.pallet || data;
      const msg = removedCount
        ? `Убрано выбывших: ${removedCount}`
        : 'Выбывших позиций не найдено';
      setNotice(msg);
      notifySuccess(msg);
      if (detail?.id === pallet.id) {
        setDetail(result);
      }
      if (fromCloseError && removedCount) {
        setCloseError('');
      }
      await load();
      if (detail?.id === pallet.id || closeTarget?.id === pallet.id) {
        try {
          const refreshed = await axios.get(`${apiBase}/pallets/${pallet.id}`, {
            params: withAccountParams(marketplace, accountId),
          });
          const next = refreshed.data?.pallet || refreshed.data;
          if (detail?.id === pallet.id) setDetail(next);
          if (closeTarget?.id === pallet.id) setCloseTarget(next);
        } catch (_ignore) {
          /* list already refreshed */
        }
      }
    } catch (requestError) {
      const msg = formatPanelError(requestError);
      setError(msg);
      if (fromCloseError) setCloseError(msg);
      notifyError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const removeOneItem = async (pallet, item) => {
    if (!pallet?.id || !item?.id) return;
    setBusyId(pallet.id);
    setError('');
    try {
      const { data } = await axios.delete(
        `${apiBase}/pallets/${pallet.id}/items/${item.id}`,
        {
          params: withAccountParams(marketplace, accountId),
          data: withAccountBody(marketplace, accountId),
        },
      );
      const result = data?.pallet || data;
      const msg = isYandex ? 'Грузоместо убрано с палеты' : 'Заказ убран с палеты';
      setNotice(msg);
      notifySuccess(msg);
      setDetail(result);
      await load();
    } catch (requestError) {
      const msg = formatPanelError(requestError);
      setError(msg);
      notifyError(msg);
    } finally {
      setBusyId(null);
    }
  };

  const detailDepartedCount = (detail?.items || []).filter(isDepartedPalletItem).length;
  const detailIsOpen = detail?.status === 'open';
  const columns = palletColumns(marketplace);
  const itemsCountLabel = isYandex ? 'грузомест' : 'заказов';
  const itemsCountChipLabel = isYandex ? 'Грузомест' : 'Заказов';

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap">
        {isLocalWarehouse && canManage ? (
          <Button
            variant="contained"
            onClick={createPallet}
            disabled={creating || !accountId}
          >
            {creating ? <CircularProgress size={18} color="inherit" /> : 'Новая палета'}
          </Button>
        ) : <span />}
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Статус"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 170 }}
          >
            {PALLET_STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Обновить
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)' }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align || 'left'}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={sort.key === column.key}
                        direction={sort.key === column.key ? sort.dir : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((pallet) => {
                const warehouseId = pallet.warehouse_id == null ? '' : String(pallet.warehouse_id);
                const warehouseName = warehouseNames[warehouseId]
                  || pallet.warehouse_name
                  || (warehouseId ? `Склад №${warehouseId}` : 'Склад не определён');
                const cargo = cargoTypeInfo(pallet.cargo_type);
                const busy = busyId === pallet.id;
                return (
                  <TableRow key={pallet.id} hover>
                    <TableCell sx={{ width: 44, pr: 0 }}>
                      <Tooltip title="Открыть состав палеты">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Открыть состав палеты"
                            onClick={() => openDetail(pallet)}
                            disabled={busy}
                          >
                            {busy ? <CircularProgress size={18} /> : <ViewIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="titleMedium" fontWeight={700}>
                        {pallet.code || `#${pallet.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p">
                        {pallet.barcode || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {Number(pallet.items_count) || 0} {itemsCountLabel}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>{warehouseName}</Typography>
                      {!isLocalWarehouse && (
                        <Chip size="small" label={cargo.label} sx={{ ...cargoChipSx, mt: 0.375 }} />
                      )}
                      {isOzon && pallet.delivery_method_id != null && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Метод доставки: {pallet.delivery_method_id}
                        </Typography>
                      )}
                      {isYandex && pallet.warehouse_id != null && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Склад: {pallet.warehouse_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FulfillmentStatusChip
                        kind="pallet"
                        status={pallet.status}
                        shipmentError={pallet.shipment_error}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {canManage && pallet.status === 'open' && (
                        Number(pallet.items_count) > 0 ? (
                          <Tooltip title="Закрыть палету">
                            <span>
                              <IconButton
                                color="primary"
                                onClick={() => openCloseDialog(pallet)}
                                disabled={busy}
                                aria-label="Закрыть палету"
                              >
                                {busy ? <CircularProgress size={20} /> : <CloseIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Отменить палету">
                            <span>
                              <IconButton
                                color="error"
                                onClick={() => setCancelTarget(pallet)}
                                disabled={busy}
                                aria-label="Отменить палету"
                              >
                                {busy ? <CircularProgress size={20} /> : <CancelPalletIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )
                      )}
                      {canManage && pallet.status === 'closed' && (
                        <Tooltip title="Переоткрыть палету">
                          <span>
                            <IconButton
                              color="warning"
                              onClick={() => askReopenPallet(pallet)}
                              disabled={busy}
                              aria-label="Переоткрыть палету"
                            >
                              {busy ? <CircularProgress size={20} /> : <ReopenIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                    Палет пока нет
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_event, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            const value = Number(event.target.value);
            localStorage.setItem(PAGE_SIZE_KEY, String(value));
            setRowsPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Paper>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Палета {detail?.code || detail?.id}
          {detail?.barcode && (
            <Typography variant="bodySmall" color="text.secondary" component="p">
              ШК {detail.barcode}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <FulfillmentStatusChip
                  kind="pallet"
                  status={detail.status}
                  shipmentError={detail.shipment_error}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${itemsCountChipLabel}: ${detail.items_count ?? (detail.items || []).length}`}
                />
                {detailDepartedCount > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`Выбывших: ${detailDepartedCount}`}
                  />
                )}
              </Stack>
              {detail.shipment_error && (
                <Alert severity="error">
                  Ошибка отгрузки МС: {detail.shipment_error}
                </Alert>
              )}
              {canManage && detailIsOpen && detailDepartedCount > 0 && (
                <Alert
                  severity="warning"
                  action={(
                    <Button
                      color="inherit"
                      size="small"
                      disabled={busyId === detail.id}
                      onClick={() => removeDeparted(detail)}
                    >
                      Убрать выбывшие
                    </Button>
                  )}
                >
                  На палете есть отменённые или выбывшие заказы — уберите их перед закрытием.
                </Alert>
              )}
              <TableContainer>
                <Table size="small" sx={TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>Фото</TableCell>
                      <TableCell>{isOzon ? 'Отправление' : isYandex ? 'Грузоместо' : 'Заказ WB'}</TableCell>
                      <TableCell>Артикул</TableCell>
                      <TableCell>{isLocalWarehouse ? 'ШК' : 'Стикер'}</TableCell>
                      <TableCell>Статус</TableCell>
                      {canManage && detailIsOpen && (
                        <TableCell align="right" width={56}> </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.items || []).map((item) => {
                      const orderId = item.posting_number
                        || item.yandex_order_id
                        || item.posting?.yandex_order_id
                        || item.wb_order_id
                        || item.posting?.posting_number
                        || '—';
                      const placeCode = isYandex
                        ? (item.place_code
                          || (item.place_index && orderId !== '—'
                            ? `${orderId}-${item.place_index}`
                            : null))
                        : null;
                      const orderLabel = placeCode || orderId;                      const article = item.task?.article
                        || item.offer_id
                        || item.posting?.product?.article
                        || item.posting?.offer_id
                        || item.sku
                        || item.posting?.sku
                        || item.task?.nm_id
                        || '—';
                      const statusInfo = palletItemStatusInfo(item);
                      const sticker = palletItemStickerParts(item);
                      const productBarcode = item.barcode
                        || item.posting?.product?.barcode
                        || item.posting?.barcode
                        || item.task?.barcode
                        || null;
                      return (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <FulfillmentProductThumb
                              task={item.task || item.posting}
                              product={item.posting?.product}
                              imageUrl={item.image_url || item.posting?.image_url || item.posting?.product?.image_url}
                              marketplace={marketplace}
                              size={44}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="bodyMedium" fontWeight={700}>{orderLabel}</Typography>
                            {isYandex && placeCode && orderId !== '—' && (
                              <Typography variant="caption" color="text.secondary" component="p">
                                Заказ {orderId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="bodyMedium" fontWeight={700}>{article}</Typography>
                          </TableCell>
                          <TableCell>
                            {isLocalWarehouse ? (
                              <Typography variant="bodyMedium" fontWeight={700}>
                                {productBarcode || '—'}
                              </Typography>
                            ) : (
                              <Stack spacing={0} sx={{ minWidth: 0 }}>
                                <Typography variant="bodyMedium" fontWeight={700}>
                                  {sticker.number || sticker.barcode || '—'}
                                </Typography>
                                {Boolean(sticker.number && sticker.barcode) && (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {sticker.barcode}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            <FulfillmentStatusChip
                              code={statusInfo.code}
                              label={statusInfo.label}
                            />
                          </TableCell>
                          {canManage && detailIsOpen && (
                            <TableCell align="right">
                              <Tooltip title="Убрать с палеты">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    aria-label="Убрать с палеты"
                                    disabled={busyId === detail.id}
                                    onClick={() => askRemoveItem(detail, item)}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {!(detail.items || []).length && (
                      <TableRow>
                        <TableCell colSpan={canManage && detailIsOpen ? 6 : 5} align="center" sx={{ py: 4 }}>
                          {isYandex ? 'В палете пока нет грузомест' : 'В палете пока нет заказов'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {canManage && detailIsOpen && detailDepartedCount > 0 && (
            <Button
              color="warning"
              disabled={busyId === detail?.id}
              onClick={() => removeDeparted(detail)}
            >
              Убрать выбывшие
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => { if (busyId !== cancelTarget?.id) setCancelTarget(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Отменить палету {cancelTarget?.code || cancelTarget?.id}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Палета пустая и больше не будет участвовать в работе: в погрузку её добавить нельзя,
            в списках она появится только по фильтру «Отменённые». Действие необратимо.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelTarget(null)}
            disabled={busyId === cancelTarget?.id}
          >
            Не отменять
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => cancelPallet(cancelTarget)}
            disabled={busyId === cancelTarget?.id}
          >
            {busyId === cancelTarget?.id ? <CircularProgress size={20} /> : 'Отменить палету'}
          </Button>
        </DialogActions>
      </Dialog>

      <PalletCloseDialog
        open={Boolean(closeTarget)}
        pallet={closeTarget}
        requireScan={false}
        step={closeStep}
        onStepChange={setCloseStep}
        busy={closeBusy}
        error={closeError}
        onClearError={() => setCloseError('')}
        scannedCode={closeVerifyCode}
        onScannedCodeChange={setCloseVerifyCode}
        onReprint={reprintPalletLabel}
        canPrint={Boolean(printerUid)}
        onCloseConfirm={closePallet}
        onCancelPallet={canManage ? (pallet) => setCancelTarget(pallet || closeTarget) : null}
        onCancel={cancelCloseDialog}
        onRemoveDeparted={canManage
          ? (pallet) => removeDeparted(pallet || closeTarget, { fromCloseError: true })
          : null}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </Stack>
  );
}
